'use client'

import { useState, useEffect } from 'react'
import { RefreshCw, HelpCircle, Eye, EyeOff } from 'lucide-react'
import Image from 'next/image'
import platformLogo from '@/public/logo.png'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Alert, AlertDescription } from '@/components/ui/alert'
import Api from '@/api/h5Api'

interface ApiResponse<T = any> {
  code: number
  message?: string
  data: T
}

interface RegisterDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onLoginClick?: () => void
}

export function RegisterDialog({
  open,
  onOpenChange,
  onLoginClick,
}: RegisterDialogProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [registerError, setRegisterError] = useState<string | null>(null)

  // 表单状态
  const [email, setEmail] = useState('')
  const [emailCode, setEmailCode] = useState('')
  const [nickname, setNickname] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // 邮箱验证码倒计时
  const [countdown, setCountdown] = useState(0)
  const [isSendingCode, setIsSendingCode] = useState(false)

  // 验证邮箱格式
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // 发送邮箱验证码
  const handleSendEmailCode = async () => {
    setRegisterError(null)

    if (email.trim() === '') {
      setRegisterError('邮箱不能为空')
      return
    }

    if (!validateEmail(email)) {
      setRegisterError('邮箱格式不正确')
      return
    }

    setIsSendingCode(true)
    try {
      const res = (await Api.userSendEmailCode(
        { username: email },
      )) as unknown as ApiResponse

      if (res.code === 200) {
        setCountdown(60)
        setRegisterError(null)
      }
    } catch (error: any) {
      console.error('发送验证码失败:', error)
      setRegisterError(error?.message || '发送验证码失败，请稍后重试')
    } finally {
      setIsSendingCode(false)
    }
  }

  // 处理注册
  const handleRegister = async () => {
    setRegisterError(null)

    if (email.trim() === '') {
      setRegisterError('邮箱不能为空')
      return
    }

    if (!validateEmail(email)) {
      setRegisterError('邮箱格式不正确')
      return
    }

    if (emailCode.trim() === '') {
      setRegisterError('邮箱验证码不能为空')
      return
    }

    if (nickname.trim() === '') {
      setRegisterError('昵称不能为空')
      return
    }

    if (nickname.length < 2 || nickname.length > 10) {
      setRegisterError('昵称长度应在2-10个字符之间')
      return
    }

    if (password.trim() === '') {
      setRegisterError('密码不能为空')
      return
    }

    if (password.length < 6 || password.length > 20) {
      setRegisterError('密码长度应在6-20个字符之间')
      return
    }

    setIsLoading(true)
    try {
      const res = (await Api.userAiAdd({
        username: email,
        emailCode,
        nickname,
        password,
      })) as unknown as ApiResponse

      if (res.code === 200) {
        // 注册成功，直接关闭对话框
        onOpenChange(false)
      }
    } catch (error: any) {
      console.error('注册失败:', error)
      setRegisterError(error?.message || '注册失败，请稍后重试')
    } finally {
      setIsLoading(false)
    }
  }

  // Enter 键注册
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleRegister()
    }
  }

  // 倒计时效果
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  // 当对话框关闭时重置表单
  useEffect(() => {
    if (!open) {
      setEmail('')
      setEmailCode('')
      setNickname('')
      setPassword('')
      setShowPassword(false)
      setRegisterError(null)
      setCountdown(0)
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="relative w-10 h-10 rounded-xl overflow-hidden">
                <Image
                  src={platformLogo}
                  alt="AI智能学习平台"
                  fill
                  className="object-contain"
                />
              </div>
              <h1 className="text-lg font-bold text-primary">AI智能学习平台</h1>
            </div>
            创建您的账号
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Error Alert */}
          {registerError && (
            <Alert variant="destructive">
              <AlertDescription>{registerError}</AlertDescription>
            </Alert>
          )}

          {/* Email Input */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="email" className="text-sm font-medium">
                邮箱
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-xs">
                      验证码可能会进入垃圾邮件箱，请注意查收
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              id="email"
              type="email"
              placeholder="请输入邮箱"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              className="focus:ring-primary focus:border-primary"
              autoFocus
            />
          </div>

          {/* Email Code Input */}
          <div className="space-y-2">
            <Label htmlFor="emailCode" className="text-sm font-medium">
              邮箱验证码
            </Label>
            <div className="flex gap-2">
              <Input
                id="emailCode"
                type="text"
                placeholder="请输入验证码"
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={6}
                className="focus:ring-primary focus:border-primary flex-1"
              />
              <Button
                type="button"
                onClick={handleSendEmailCode}
                disabled={countdown > 0 || isSendingCode}
                variant="outline"
                className="flex-shrink-0 min-w-[100px]"
              >
                {isSendingCode ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : countdown > 0 ? (
                  `${countdown}s`
                ) : (
                  '获取验证码'
                )}
              </Button>
            </div>
          </div>

          {/* Nickname Input */}
          <div className="space-y-2">
            <Label htmlFor="nickname" className="text-sm font-medium">
              昵称
            </Label>
            <Input
              id="nickname"
              type="text"
              placeholder="请输入昵称（2-10个字符）"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={10}
              className="focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Password Input */}
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium">
              密码
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="请输入密码（6-20个字符）"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={20}
                className="focus:ring-primary focus:border-primary pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-300"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Register Button */}
          <Button
            onClick={handleRegister}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                注册中...
              </span>
            ) : (
              '立即注册'
            )}
          </Button>

          {/* Login Link */}
          <div className="flex items-center justify-center pt-2 text-sm border-t">
            <span className="text-muted-foreground">已经有账号？</span>
            <button
              className="text-primary hover:text-primary/80 transition-colors ml-1 font-medium"
              onClick={() => {
                onOpenChange(false)
                onLoginClick?.()
              }}
            >
              立即登录
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
